import { Injectable } from '@angular/core';
import { IProductDataV2, IProductDataV3, Product } from '@df/catalog/product/product';
import { StructuralData } from '@df/core/meta/structural-data/structural-data.namespace';
import { StructuralDataService } from '@df/core/meta/structural-data/structural-data.service';
import { optionLabelPipe } from '@df/ui/common/option-label.pipe';
import isEmpty from 'lodash-es/isEmpty';
import { ProductDecorator } from '../../../../catalog/model/product/product.decorator';

declare module '@df/core/meta/structural-data/structural-data.namespace' {
    namespace StructuralData {
        export interface IProductData extends IData<Type.product> {
            isVariantOf?: IProductParentVariantData;
            productGroupID?: number | string;
            variesBy?: string[];
            hasVariant?: IProductData[];
            size?: string | number | undefined;
        }

        export interface IGroupProductData extends Omit<IProductData, '@type'> {
            '@type': IData<Type.productGroup>['@type'];
            size?: string | number | undefined;
        }

        export interface IProductParentVariantData {
            '@type': IData<Type.productGroup>['@type'];
            '@id': string;
            name: string;
        }
    }
}

@Injectable({
    providedIn: 'root'
})
export class StructuralDataServiceDecorator extends StructuralDataService {
    override getOrganizationData(): StructuralData.IOrganizationData {
        const superData = super.getOrganizationData();

        superData.sameAs = [
            'https://www.facebook.com/oliverbonas',
            'https://www.twitter.com/oliverbonas',
            'https://www.instagram.com/oliverbonas',
            'https://www.pinterest.com/oliverbonas',
            'https://www.oliverbonas.com/blog'
        ];

        return superData;
    }

    /**
     * Skip reviews
     */
    override addOrganizationData() {
        const data = this.getOrganizationData();

        this.add(data);
    }

    override getProductData(product: Product<IProductDataV3 | IProductDataV2>): StructuralData.IProductData {
        const productData = product.data as IProductDataV3;

        // If configurable, build IGroupProductData
        if (product.isConfigurable) {
            const groupData: StructuralData.IGroupProductData = {
                '@context': 'http://schema.org',
                '@type': StructuralData.Type.productGroup,
                name: String(productData.name ?? ''),
                sku: productData.sku !== undefined ? String(productData.sku) : '',
                url: this.url.toUrl(productData.url || ''),
                productGroupID:
                    product.parent.data?.sku !== undefined
                        ? String(product.parent.data.sku)
                        : product.data?.sku !== undefined
                          ? String(product.data.sku)
                          : '',
                variesBy: ['https://schema.org/size'],
                hasVariant: []
            };

            const productChildren = product.children as ProductDecorator<IProductDataV3>[];
            const uniqueChildren = productChildren.filter(
                (child, index, self) => index === self.findIndex(c => c.data?.sku === child.data?.sku)
            );

            groupData.hasVariant = uniqueChildren.map(child =>
                this.getProductChildData(child as ProductDecorator<IProductDataV3>)
            ) as StructuralData.IProductData[];

            groupData.variesBy = groupData.variesBy ?? [];
            groupData.hasVariant = groupData.hasVariant ?? [];

            return this.getProductCommonData(product, groupData) as unknown as StructuralData.IProductData;
        }

        // Simple product
        type ProductDataWithSize = StructuralData.IProductData & { size?: string };
        const data: ProductDataWithSize = {
            '@context': 'http://schema.org',
            '@type': StructuralData.Type.product,
            name: String(productData.name ?? ''),
            sku: productData.sku !== undefined ? String(productData.sku) : '',
            url: this.url.toUrl(productData.url || '')
        };

        if (product.isSimple && product.parent.data?.sku && product.parent.id !== product.id) {
            data.isVariantOf = {
                name: String(product.parent.data?.name ?? ''),
                '@type': StructuralData.Type.productGroup,
                '@id': product.parent.data?.sku !== undefined ? String(product.parent.data.sku) : ''
            };
        }

        return this.getProductCommonData(product, data);
    }

    protected override setListingTracking() {
        // do not set tracking for listing
    }

    protected override getAppData(): StructuralData.IAppData {
        const superAppData = super.getAppData();

        superAppData.potentialAction['query-input'] = 'search_term_string';

        return superAppData;
    }

    private getProductCommonData(
        product: Product<IProductDataV3 | IProductDataV2>,
        data: StructuralData.IGroupProductData | StructuralData.IProductData
    ): StructuralData.IProductData {
        const productData = product.data as IProductDataV3;
        const rawDescription = productData.description || (product.parent.data as IProductDataV3)?.description;
        const cleanDescription = rawDescription ? rawDescription.replace(/(<([^>]+)>)/gi, ' ') : rawDescription;
        const meta = productData.meta;
        const { value: sizeValue, key: sizeKey } = this.getFirstPopulatedSize(
            productData as unknown as Record<string, number | string | number[] | undefined>
        );

        const image = meta ? meta.ogimage || productData.image : productData.image;

        if (image) {
            data.image = [this.app.url.toMediaUrl(image)].filter(Boolean);
        } else {
            data.image = [];
        }

        if (cleanDescription) {
            data.description = cleanDescription;
        }

        const brandFilterAttr = productData.brand_filter;
        const brand = optionLabelPipe(brandFilterAttr, 'brand_filter')?.toString();

        if (brand) {
            data.brand = {
                '@type': StructuralData.Type.brand,
                name: brand || 'Oliver Bonas'
            };
        }

        if (sizeValue) {
            const size = optionLabelPipe(sizeValue, sizeKey) as string;

            if (!isEmpty(size)) {
                data.size = size;
            }
        }

        if (productData.price) {
            data.offers = {
                '@type': StructuralData.Type.offer,
                priceCurrency: this.app.data.currency.base,
                price: productData.price ? productData.price.toString() : '',
                itemCondition: StructuralData.ItemCondition.new,
                availability: this.app.meta.getProductAvailability(product.id) as StructuralData.ItemAvailability
            };
        }

        return data as unknown as StructuralData.IProductData;
    }

    /**
     * Returns the first populated size attribute and its key, falling back to 'size'.
     */
    private getFirstPopulatedSize(productData: Record<string, number | string | number[] | undefined>): {
        value: number | string | number[] | undefined;
        key: string;
    } {
        const sizeAttributes = [
            'print_size',
            'textile_size',
            'shoe_size',
            'frame_size',
            'ring_size',
            'size_xs_xxxl',
            'accessory_size',
            'size' // fallback
        ];
        for (const key of sizeAttributes) {
            const value = productData[key];

            if (value !== undefined) {
                return { value, key };
            }
        }
        return { value: undefined, key: 'size' };
    }

    private getProductChildData(product: ProductDecorator<IProductDataV3 | IProductDataV2>): StructuralData.IProductData {
        const productData = product.data as IProductDataV3;
        const rawDescription = productData.description || (product.parent.data as IProductDataV3)?.description;
        const cleanDescription = rawDescription ? rawDescription.replace(/(<([^>]+)>)/gi, ' ') : rawDescription;
        const { value: sizeValue, key: sizeKey } = this.getFirstPopulatedSize(
            productData as unknown as Record<string, number | string | number[] | undefined>
        );

        // Extend IProductData to allow 'size' property assignment
        type ProductDataWithSize = StructuralData.IProductData & { size?: string };
        const data: ProductDataWithSize = {
            '@context': 'http://schema.org',
            '@type': StructuralData.Type.product,
            sku: productData.sku,
            url: this.url.toUrl(productData.url || ''),
            image: productData.image ? [this.app.url.toMediaUrl(productData.image)].filter(Boolean) : [],
            name: productData.name,
            description: cleanDescription,
            offers: undefined
        };

        if (sizeValue) {
            const size = optionLabelPipe(sizeValue, sizeKey) as string;

            if (!isEmpty(size)) {
                data.size = size;
            }
        }

        if (productData.price) {
            data.offers = {
                '@type': StructuralData.Type.offer,
                priceCurrency: this.app.data.currency.base,
                price: productData.price ? productData.price.toString() : '',
                itemCondition: StructuralData.ItemCondition.new,
                availability: this.app.meta.getProductAvailability(product.id) as StructuralData.ItemAvailability
            };
        }

        return data;
    }
}
